/**
 * SalesEinstellungen — SMTP/IMAP, Scoring, Workflows Config.
 *
 * @page /sales/einstellungen
 * @roles admin, manager
 */
import { useState, useEffect } from 'react';
import useTabParam from '../../hooks/useTabParam';
import { useLanguage } from '../../context/LanguageContext';
import { ToggleSwitch } from '../../components/ui';
import { Check, X, Plus, Trash2, RefreshCw, Upload, Download, FileText, Image, Bot, Edit3, Save, ExternalLink, AlertCircle, GripVertical, Star, Eye, EyeOff, Loader2 } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(`${API}${path}`, {
    ...opts, credentials: 'include',
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  });
}

function TabButton({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-[8px] text-[13px] font-medium transition-all ${
        active ? 'bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
      }`}
    >
      {label}
    </button>
  );
}

function SmtpField({ label, field, type = 'text', cfg, setCfg }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">{label}</label>
      <input
        type={type}
        value={cfg[field] || ''}
        onChange={(e) => setCfg(prev => ({ ...prev, [field]: e.target.value }))}
        className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)]"
      />
    </div>
  );
}

function PasswordField({ label, field, cfg, setCfg }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={cfg[field] || ''}
          onChange={(e) => setCfg(prev => ({ ...prev, [field]: e.target.value }))}
          className="w-full px-3 py-2 pr-9 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)]"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
        >
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

// #1042 — Per-Section Save-Button + inline Toast
function SectionSaveBar({ saving, toast, onSave, label }) {
  return (
    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[var(--border-card)]">
      <button
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-white text-[13px] font-medium disabled:opacity-50"
        style={{ background: 'var(--primary)' }}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {saving ? '...' : (label || 'Speichern')}
      </button>
      {toast && (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium ${toast.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {toast.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
          <span>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}

function SmtpImapTab() {
  const { t } = useLanguage();
  const [cfg, setCfg] = useState({});
  const [loading, setLoading] = useState(true);
  // #1042 — Per-Section Saving + Toast (statt einem globalen testResult)
  const [savingSection, setSavingSection] = useState(null);
  const [toasts, setToasts] = useState({}); // SAVE-Toasts in SectionSaveBar { smtp, imap, allgemein, globalId, genericId }
  // #1042 NACHTRAG Bug 1 — separate Test-Toasts direkt neben Test-Buttons
  const [testToasts, setTestToasts] = useState({}); // { smtp, imap }
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  // #1042 NACHTRAG Bug 3 — Polling-Status sichtbar nach Restart
  const [pollingStatus, setPollingStatus] = useState(null); // { enabled, intervalSec, restartedAt }
  const [signatur, setSignatur] = useState('');
  const [signaturSaving, setSignaturSaving] = useState(false);
  const [signaturMsg, setSignaturMsg] = useState('');
  const [showSigPreview, setShowSigPreview] = useState(false);
  // #1040 — IMAP Manueller Trigger + Log
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const [imapLog, setImapLog] = useState([]);

  const loadImapLog = () => {
    api('/api/sales/settings/imap-log').then(setImapLog).catch(() => {});
  };

  useEffect(() => {
    api('/api/sales/settings').then(setCfg).catch(console.error).finally(() => setLoading(false));
    api('/api/sales/einstellungen/signatur').then(d => setSignatur(d.signatur || '')).catch(() => {});
    loadImapLog();
  }, []);

  // #1040 — Manueller Poll-Trigger
  const pollNow = async () => {
    setPollResult(null);
    setPolling(true);
    try {
      const res = await api('/api/sales/settings/poll-now', { method: 'POST' });
      setPollResult({ ok: true, ...res });
      loadImapLog();
      setTimeout(() => setPollResult(null), 8000);
    } catch (err) {
      setPollResult({ ok: false, error: err.message });
      setTimeout(() => setPollResult(null), 8000);
    } finally {
      setPolling(false);
    }
  };

  const saveSignatur = async () => {
    setSignaturSaving(true);
    try {
      await api('/api/sales/einstellungen/signatur', { method: 'PUT', body: { signatur } });
      setSignaturMsg(t('common.saved') || 'Gespeichert');
      setTimeout(() => setSignaturMsg(''), 3000);
    } catch (err) {
      setSignaturMsg(err.message);
    } finally {
      setSignaturSaving(false);
    }
  };

  // #1042 — Helper: Toast pro Section setzen mit Auto-Dismiss
  const setToast = (section, ok, msg, ttlMs = 4000) => {
    setToasts(prev => ({ ...prev, [section]: { ok, msg } }));
    setTimeout(() => setToasts(prev => ({ ...prev, [section]: null })), ttlMs);
  };

  // #1042 NACHTRAG Bug 1 — Test-Toast direkt neben Test-Button (separate State)
  const setTestToast = (section, ok, msg, ttlMs = 5000) => {
    setTestToasts(prev => ({ ...prev, [section]: { ok, msg } }));
    setTimeout(() => setTestToasts(prev => ({ ...prev, [section]: null })), ttlMs);
  };

  // #1042 — Section-spezifischer Save: nur die jeweiligen Keys werden geschickt
  const saveSection = async (sectionName, keys, opts = {}) => {
    setSavingSection(sectionName);
    setToasts(prev => ({ ...prev, [sectionName]: null }));
    try {
      const body = {};
      keys.forEach(k => { if (cfg[k] !== undefined) body[k] = cfg[k]; });
      await api('/api/sales/settings', { method: 'PUT', body });
      // #1042 — IMAP-Section: Polling neu starten + Intervall im Toast zeigen
      let extraMsg = '';
      if (opts.restartPolling) {
        try {
          const r = await api('/api/sales/settings/restart-polling', { method: 'POST' });
          if (r && typeof r.intervalSec === 'number') {
            extraMsg = r.enabled
              ? ` (Polling: ${r.intervalSec}s)`
              : ` (Polling deaktiviert)`;
            // #1042 NACHTRAG Bug 3 — Polling-Status persistent anzeigen
            setPollingStatus({ enabled: r.enabled, intervalSec: r.intervalSec, restartedAt: new Date() });
          }
        } catch (e) {
          extraMsg = ` (Polling-Restart fehlgeschlagen: ${e.message})`;
        }
      }
      setToast(sectionName, true, (t('sales.settings.savedToast') || 'Gespeichert') + extraMsg);
    } catch (err) {
      setToast(sectionName, false, `${t('sales.settings.saveFailed') || 'Speichern fehlgeschlagen'}: ${err.message}`, 6000);
    } finally {
      setSavingSection(null);
    }
  };

  const testSmtp = async () => {
    setTestToasts(prev => ({ ...prev, smtp: null }));
    setTestingSmtp(true);
    try {
      // #1042 — Pass leer schicken wenn Platzhalter, Backend lädt aus DB nach
      const passToSend = (cfg.smtp_pass === '••••••••') ? '' : (cfg.smtp_pass || '');
      const res = await api('/api/sales/settings/test-smtp', {
        method: 'POST',
        body: { host: cfg.smtp_host, port: cfg.smtp_port, user: cfg.smtp_user, pass: passToSend },
      });
      setTestToast('smtp', res.ok,
        res.ok
          ? (t('sales.settings.smtpOkToast') || 'SMTP-Verbindung erfolgreich')
          : `${t('sales.settings.smtpFailed') || 'SMTP fehlgeschlagen'}: ${res.error || 'unbekannt'}`,
        5000
      );
    } catch (err) {
      setTestToast('smtp', false, `${t('sales.settings.smtpFailed') || 'SMTP fehlgeschlagen'}: ${err.message}`, 6000);
    } finally {
      setTestingSmtp(false);
    }
  };

  const testImap = async () => {
    setTestToasts(prev => ({ ...prev, imap: null }));
    setTestingImap(true);
    try {
      // #1042 — Pass leer schicken wenn Platzhalter, Backend lädt aus DB nach
      const passToSend = (cfg.imap_pass === '••••••••') ? '' : (cfg.imap_pass || '');
      const res = await api('/api/sales/settings/test-imap', {
        method: 'POST',
        body: { host: cfg.imap_host, port: cfg.imap_port, user: cfg.imap_user, pass: passToSend, tls: cfg.imap_tls },
      });
      setTestToast('imap', res.ok,
        res.ok
          ? (t('sales.settings.imapOkToast') || 'IMAP-Verbindung erfolgreich')
          : `${t('sales.settings.imapFailed') || 'IMAP fehlgeschlagen'}: ${res.error || 'unbekannt'}`,
        5000
      );
    } catch (err) {
      setTestToast('imap', false, `${t('sales.settings.imapFailed') || 'IMAP fehlgeschlagen'}: ${err.message}`, 6000);
    } finally {
      setTestingImap(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-[var(--text-tertiary)]">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* SMTP */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-4">SMTP (Ausgehend)</h3>
        <div className="grid grid-cols-2 gap-3">
          <SmtpField label="Host" field="smtp_host" cfg={cfg} setCfg={setCfg} />
          <SmtpField label="Port" field="smtp_port" cfg={cfg} setCfg={setCfg} />
          <SmtpField label="User" field="smtp_user" cfg={cfg} setCfg={setCfg} />
          <SmtpField label="Passwort" field="smtp_pass" type="password" cfg={cfg} setCfg={setCfg} />
          <SmtpField label="From E-Mail" field="from_email" cfg={cfg} setCfg={setCfg} />
          <SmtpField label="From Name" field="from_name" cfg={cfg} setCfg={setCfg} />
        </div>
        <div className="flex gap-2 mt-4 items-center flex-wrap">
          <button
            onClick={testSmtp}
            disabled={testingSmtp || savingSection === 'smtp'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] border border-[var(--border-input)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50"
          >
            {testingSmtp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {testingSmtp ? (t('sales.settings.testing') || 'Teste...') : t('sales.settings.testConnection')}
          </button>
          {/* #1042 NACHTRAG Bug 1 — Inline Test-Toast direkt neben Test-Button */}
          {testToasts.smtp && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium ${testToasts.smtp.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {testToasts.smtp.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
              <span>{testToasts.smtp.msg}</span>
            </div>
          )}
        </div>
        <SectionSaveBar
          saving={savingSection === 'smtp'}
          toast={toasts.smtp}
          onSave={() => saveSection('smtp', ['smtp_host', 'smtp_port', 'smtp_user', 'smtp_pass', 'from_email', 'from_name'])}
          label={t('common.save') || 'Speichern'}
        />
      </div>

      {/* IMAP */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">{t('sales.settings.imapTitle')}</h3>
          <ToggleSwitch
            checked={cfg.imap_enabled === 'true'}
            onChange={() => setCfg(prev => ({ ...prev, imap_enabled: prev.imap_enabled === 'true' ? 'false' : 'true' }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <SmtpField label={t('sales.settings.imapHost')} field="imap_host" cfg={cfg} setCfg={setCfg} />
          <SmtpField label={t('sales.settings.imapPort')} field="imap_port" cfg={cfg} setCfg={setCfg} />
          <SmtpField label={t('sales.settings.imapUser')} field="imap_user" cfg={cfg} setCfg={setCfg} />
          <PasswordField label={t('sales.settings.imapPass')} field="imap_pass" cfg={cfg} setCfg={setCfg} />
          <div>
            <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">{t('sales.settings.imapTls')}</label>
            <select
              value={cfg.imap_tls || 'true'}
              onChange={(e) => setCfg({ ...cfg, imap_tls: e.target.value })}
              className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)]"
            >
              <option value="true">Ja</option>
              <option value="false">Nein</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">{t('sales.settings.replyDomain')}</label>
            <input
              type="text"
              value={cfg.reply_domain || 'email.weship.eu'}
              onChange={(e) => setCfg(prev => ({ ...prev, reply_domain: e.target.value }))}
              className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)]"
            />
            <p className="text-[12px] text-[var(--text-tertiary)] mt-1">{t('sales.settings.replyDomainHint')}</p>
          </div>
          {/* #1040 — Polling-Intervall + #1042 NACHTRAG Bug 3 Status-Pill */}
          <div>
            <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">{t('sales.settings.imapInterval') || 'Polling-Intervall'}</label>
            <select
              value={cfg.imap_polling_interval || '300'}
              onChange={(e) => setCfg({ ...cfg, imap_polling_interval: e.target.value })}
              className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)]"
            >
              <option value="60">1 {t('sales.settings.imapMinute') || 'Minute'}</option>
              <option value="120">2 {t('sales.settings.imapMinutes') || 'Minuten'}</option>
              <option value="300">5 {t('sales.settings.imapMinutes') || 'Minuten'} ({t('common.default') || 'Standard'})</option>
              <option value="600">10 {t('sales.settings.imapMinutes') || 'Minuten'}</option>
              <option value="1800">30 {t('sales.settings.imapMinutes') || 'Minuten'}</option>
              <option value="3600">60 {t('sales.settings.imapMinutes') || 'Minuten'}</option>
            </select>
            <p className="text-[12px] text-[var(--text-tertiary)] mt-1">{t('sales.settings.imapIntervalHint') || 'Wie oft das Postfach automatisch abgerufen wird (60-3600 Sek)'}</p>
            {/* #1042 NACHTRAG Bug 3 — Polling-Status sichtbar nach Save+Restart */}
            {pollingStatus && (
              <div className={`mt-2 flex items-center gap-1.5 px-2.5 py-1 rounded-[6px] text-[11px] font-medium ${pollingStatus.enabled ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-slate-50 border border-slate-200 text-slate-600'}`}>
                <Check className="w-3 h-3" />
                <span>
                  {pollingStatus.enabled
                    ? `${t('sales.settings.pollingActive') || 'Polling aktiv'}: ${pollingStatus.intervalSec}s`
                    : (t('sales.settings.pollingDisabled') || 'Polling deaktiviert')
                  }
                </span>
                <span className="text-[var(--text-tertiary)]">· {t('sales.settings.pollingRestartedAt') || 'Restart'} {pollingStatus.restartedAt.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap items-center">
          <button
            onClick={testImap}
            disabled={testingImap || savingSection === 'imap'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] border border-[var(--border-input)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50"
          >
            {testingImap ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {testingImap ? (t('sales.settings.testing') || 'Teste...') : t('sales.settings.testConnection')}
          </button>
          {/* #1042 NACHTRAG Bug 1 — Inline Test-Toast direkt neben Test-Button */}
          {testToasts.imap && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium ${testToasts.imap.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {testToasts.imap.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
              <span>{testToasts.imap.msg}</span>
            </div>
          )}
          {/* #1040 — Manueller Trigger */}
          <button
            onClick={pollNow}
            disabled={polling || cfg.imap_enabled !== 'true'}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] bg-[var(--primary)] text-white hover:opacity-90 disabled:opacity-50"
            title={cfg.imap_enabled !== 'true' ? (t('sales.settings.imapDisabledHint') || 'IMAP deaktiviert') : ''}
          >
            {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {polling ? (t('sales.settings.imapPolling') || 'Rufe ab...') : (t('sales.settings.imapPollNow') || 'Mails jetzt abrufen')}
          </button>
          {pollResult && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium ${pollResult.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {pollResult.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
              <span>
                {pollResult.ok
                  ? (t('sales.settings.imapPollResult') || '{found} gefunden, {processed} verarbeitet, {errors} Fehler')
                      .replace('{found}', pollResult.mailsFound || 0)
                      .replace('{processed}', pollResult.mailsProcessed || 0)
                      .replace('{errors}', pollResult.errors || 0)
                  : pollResult.error}
              </span>
            </div>
          )}
        </div>

        {/* #1040 — Log-Tabelle */}
        <div className="mt-5 pt-5 border-t border-[var(--border-card)]">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[13px] font-semibold text-[var(--text-primary)]">{t('sales.settings.imapLogTitle') || 'Polling-Protokoll'}</h4>
            <button
              onClick={loadImapLog}
              className="flex items-center gap-1 px-2 py-1 text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              <RefreshCw className="w-3 h-3" />
              {t('common.refresh') || 'Aktualisieren'}
            </button>
          </div>
          {imapLog.length === 0 ? (
            <div className="text-[12px] text-[var(--text-tertiary)] py-3 text-center">{t('sales.settings.imapLogEmpty') || 'Noch keine Polls protokolliert'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[var(--text-tertiary)] border-b border-[var(--border-card)]">
                    <th className="py-1.5 pr-3 font-medium">{t('sales.settings.imapLogTime') || 'Zeit'}</th>
                    <th className="py-1.5 pr-3 font-medium text-right">{t('sales.settings.imapLogFound') || 'Gefunden'}</th>
                    <th className="py-1.5 pr-3 font-medium text-right">{t('sales.settings.imapLogProcessed') || 'Verarbeitet'}</th>
                    <th className="py-1.5 pr-3 font-medium text-right">{t('sales.settings.imapLogErrors') || 'Fehler'}</th>
                    <th className="py-1.5 pr-3 font-medium text-right">{t('sales.settings.imapLogDuration') || 'Dauer'}</th>
                    <th className="py-1.5 pr-3 font-medium">{t('sales.settings.imapLogTrigger') || 'Auslöser'}</th>
                  </tr>
                </thead>
                <tbody>
                  {imapLog.slice(0, 20).map((row) => (
                    <tr key={row.id} className="border-b border-[var(--border-card)] last:border-0">
                      <td className="py-1.5 pr-3 text-[var(--text-secondary)]">
                        {new Date(row.polled_at).toLocaleString('de-AT', { hour12: false })}
                      </td>
                      <td className="py-1.5 pr-3 text-right text-[var(--text-primary)]">{row.mails_found}</td>
                      <td className="py-1.5 pr-3 text-right text-[var(--text-primary)]">{row.mails_processed}</td>
                      <td className={`py-1.5 pr-3 text-right ${row.errors > 0 ? 'text-red-600' : 'text-[var(--text-secondary)]'}`}>{row.errors}</td>
                      <td className="py-1.5 pr-3 text-right text-[var(--text-tertiary)]">{row.duration_ms}ms</td>
                      <td className="py-1.5 pr-3 text-[var(--text-tertiary)]" title={row.error_message || ''}>{row.triggered_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <SectionSaveBar
          saving={savingSection === 'imap'}
          toast={toasts.imap}
          onSave={() => saveSection('imap', ['imap_host', 'imap_port', 'imap_user', 'imap_pass', 'imap_tls', 'imap_enabled', 'imap_polling_interval', 'reply_domain'], { restartPolling: true })}
          label={t('common.save') || 'Speichern'}
        />
      </div>

      {/* Calendly + Webhook */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-4">Allgemein</h3>
        <div className="grid grid-cols-2 gap-3">
          <SmtpField label="Calendly URL" field="calendly_url" cfg={cfg} setCfg={setCfg} />
          <SmtpField label="Webhook Secret" field="webhook_secret" type="password" cfg={cfg} setCfg={setCfg} />
        </div>
        <SectionSaveBar
          saving={savingSection === 'allgemein'}
          toast={toasts.allgemein}
          onSave={() => saveSection('allgemein', ['calendly_url', 'webhook_secret'])}
          label={t('common.save') || 'Speichern'}
        />
      </div>

      {/* Firmen-Signatur (Corporate) */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-1">
          {t('sales.settings.corporateSignature')}
        </h3>
        <p className="text-[12px] text-[var(--text-secondary)] mb-4">
          {t('sales.settings.corporateSignatureHint')}
        </p>
        <textarea
          value={signatur}
          onChange={e => setSignatur(e.target.value)}
          rows={8}
          placeholder={'<p><strong>WeShip Fulfillment GmbH</strong></p>\n<p>Frikusweg 10 | 8141 Premstätten</p>'}
          className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)] resize-none font-mono"
        />
        <p className="text-[11px] text-[var(--text-tertiary)] mt-1 mb-3">{t('sales.settings.corporateSignatureHtml')}</p>

        {/* Preview */}
        {showSigPreview && signatur && (
          <div className="mb-3 p-4 rounded-[8px] border border-[var(--border-card)] bg-[var(--bg-inset)]">
            <p className="text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wide mb-2">{t('sales.settings.signaturePreview')}</p>
            <div dangerouslySetInnerHTML={{ __html: signatur }} />
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={saveSignatur}
            disabled={signaturSaving}
            className="px-4 py-2 rounded-[8px] text-white text-[13px] font-medium disabled:opacity-50"
            style={{ background: 'var(--primary)' }}
          >
            {signaturSaving ? '...' : t('common.save')}
          </button>
          <button
            onClick={() => setShowSigPreview(v => !v)}
            className="px-4 py-2 rounded-[8px] text-[13px] font-medium border border-[var(--border-card)] text-[var(--text-primary)] hover:bg-[var(--bg-inset)] transition-colors"
          >
            {t('sales.settings.signaturePreview')}
          </button>
          {signaturMsg && <span className="text-[12px] font-medium text-emerald-600">{signaturMsg}</span>}
        </div>
      </div>

      {/* #1039 — Globale Sales-Identität (anonym, mit Sales-Calendly) */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-1">
          {t('sales.settings.globalIdentityTitle') || 'Globale Sales-Identität'}
        </h3>
        <p className="text-[12px] text-[var(--text-secondary)] mb-4">
          {t('sales.settings.globalIdentityHint') || 'Wird im Workflow-Step verwendet, wenn „Global" gewählt ist. Anonyme Absender-Identität mit gemeinsamem Sales-Calendly.'}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <SmtpField label={t('sales.settings.globalGrussformel') || 'Grußformel'} field="global_grussformel" cfg={cfg} setCfg={setCfg} />
          <SmtpField label={t('sales.settings.globalName') || 'Absender-Name'} field="global_name" cfg={cfg} setCfg={setCfg} />
          <div className="col-span-2">
            <SmtpField label={t('sales.settings.globalCalendlyUrl') || 'Sales-Calendly URL'} field="global_calendly_url" cfg={cfg} setCfg={setCfg} />
          </div>
        </div>
        <SectionSaveBar
          saving={savingSection === 'globalId'}
          toast={toasts.globalId}
          onSave={() => saveSection('globalId', ['global_grussformel', 'global_name', 'global_calendly_url'])}
          label={t('common.save') || 'Speichern'}
        />
      </div>

      {/* #1039 — Generische Identität (ohne CTA) */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-1">
          {t('sales.settings.genericIdentityTitle') || 'Generische Identität'}
        </h3>
        <p className="text-[12px] text-[var(--text-secondary)] mb-4">
          {t('sales.settings.genericIdentityHint') || 'Wird im Workflow-Step verwendet, wenn „Generisch" gewählt ist. Ohne Calendly-CTA.'}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <SmtpField label={t('sales.settings.genericGrussformel') || 'Grußformel'} field="generic_grussformel" cfg={cfg} setCfg={setCfg} />
          <SmtpField label={t('sales.settings.genericName') || 'Absender-Name'} field="generic_name" cfg={cfg} setCfg={setCfg} />
        </div>
        <SectionSaveBar
          saving={savingSection === 'genericId'}
          toast={toasts.genericId}
          onSave={() => saveSection('genericId', ['generic_grussformel', 'generic_name'])}
          label={t('common.save') || 'Speichern'}
        />
      </div>
    </div>
  );
}

function ScoringTab() {
  const { t } = useLanguage();
  const [regeln, setRegeln] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = () => api('/api/sales/scoring').then(setRegeln).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggleAktiv = async (regel) => {
    await api(`/api/sales/scoring/${regel.id}`, { method: 'PUT', body: { aktiv: !regel.aktiv } });
    load();
  };

  const updatePunkte = async (regel, punkte) => {
    await api(`/api/sales/scoring/${regel.id}`, { method: 'PUT', body: { punkte: parseInt(punkte) } });
    load();
    setEditing(null);
  };

  const deleteRegel = async (id) => {
    await api(`/api/sales/scoring/${id}`, { method: 'DELETE' });
    setDeleteConfirm(null);
    load();
  };

  const addRegel = async () => {
    await api('/api/sales/scoring', { method: 'POST', body: { bezeichnung: 'Neue Regel', typ: 'keyword_plus', wert: '', punkte: 10 } });
    load();
  };

  if (loading) return <div className="text-center py-8 text-[var(--text-tertiary)]">Loading...</div>;

  return (
    <div>
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border-table,#F3F4F6)]">
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">Bezeichnung</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">Typ</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">Wert</th>
              <th className="text-center px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">Punkte</th>
              <th className="text-center px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">Aktiv</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {regeln.map((r) => (
              <tr key={r.id} className="border-b border-[var(--border-table,#F3F4F6)]">
                <td className="px-4 py-2.5 text-[13px] text-[var(--text-primary)]">{r.bezeichnung}</td>
                <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)]">{r.typ}</td>
                <td className="px-4 py-2.5 text-[12px] text-[var(--text-tertiary)] max-w-[200px] truncate">{r.wert}</td>
                <td className="px-4 py-2.5 text-center">
                  {editing === r.id ? (
                    <input
                      type="number"
                      defaultValue={r.punkte}
                      onBlur={(e) => updatePunkte(r, e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && updatePunkte(r, e.target.value)}
                      autoFocus
                      className="w-16 px-2 py-1 rounded border border-[var(--border-input)] text-center text-[12px]"
                    />
                  ) : (
                    <span
                      onClick={() => setEditing(r.id)}
                      className={`cursor-pointer font-medium text-[13px] ${r.punkte >= 0 ? 'text-emerald-600' : 'text-red-500'}`}
                    >
                      {r.punkte > 0 ? '+' : ''}{r.punkte}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-center">
                  <button onClick={() => toggleAktiv(r)}>
                    <span className={`inline-block w-3 h-3 rounded-full ${r.aktiv ? 'bg-[#338edc]' : 'bg-[var(--border-card)]'}`} />
                  </button>
                </td>
                <td className="px-2 py-2.5">
                  <button onClick={() => setDeleteConfirm(r.id)} className="text-[var(--text-tertiary)] hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        onClick={addRegel}
        className="mt-3 flex items-center gap-1.5 text-[12px] text-[var(--primary)] hover:underline"
      >
        <Plus className="w-3.5 h-3.5" /> Neue Regel
      </button>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-[var(--bg-card)] rounded-2xl p-6 w-full max-w-sm shadow-xl border border-[var(--border-card)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Regel löschen</h3>
                <p className="text-[13px] text-[var(--text-secondary)]">Diese Aktion kann nicht rückgängig gemacht werden.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] border border-[var(--border-card)] hover:bg-[var(--bg-inset)]">
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button onClick={() => deleteRegel(deleteConfirm)} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-white bg-red-600 hover:bg-red-700">
                {t('common.delete') || 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const TYPE_ICONS = { pdf: FileText, docx: FileText, pptx: FileText, png: Image, jpg: Image };

function AssetsTab() {
  const { t } = useLanguage();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = () => api('/api/sales/assets').then(setAssets).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatRelative = (d) => {
    if (!d) return '-';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `vor ${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `vor ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `vor ${days}d`;
    return new Date(d).toLocaleDateString('de-AT');
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setErrorMsg('');
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await fetch(`${API}/api/sales/assets`, {
        method: 'POST', credentials: 'include', body: form,
      });
      const ct = resp.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        if (resp.status === 413) throw new Error('Datei zu groß (max. 50MB)');
        throw new Error(`Upload fehlgeschlagen (HTTP ${resp.status})`);
      }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      setShowUpload(false);
      load();
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const downloadAsset = (id) => {
    window.open(`${API}/api/sales/assets/${id}/download`, '_blank');
  };

  const deleteAsset = async (id) => {
    setErrorMsg('');
    try {
      await api(`/api/sales/assets/${id}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      load();
    } catch (err) {
      setDeleteConfirm(null);
      setErrorMsg(err.message);
    }
  };

  if (loading) return <div className="text-center py-8 text-[var(--text-tertiary)]">Loading...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">{t('sales.assets.title')}</h3>
        <button
          onClick={() => { setShowUpload(!showUpload); setErrorMsg(''); }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-white text-[12px] font-medium"
          style={{ background: 'var(--primary)' }}
        >
          <Upload className="w-3.5 h-3.5" /> {t('sales.assets.upload')}
        </button>
      </div>

      {/* Error Message */}
      {errorMsg && (
        <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-[8px] bg-red-50 border border-red-200 text-red-700 text-[13px]">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{errorMsg}</span>
          <button onClick={() => setErrorMsg('')} className="p-0.5 hover:bg-red-100 rounded">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Upload Zone */}
      {showUpload && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`mb-4 border-2 border-dashed rounded-[12px] p-8 text-center transition-colors ${
            dragOver ? 'border-[var(--primary)] bg-primary-50/50' : 'border-[var(--border-input)] bg-[var(--bg-inset)]'
          }`}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-[var(--primary)] border-t-transparent rounded-full" />
              <span className="text-[13px] text-[var(--text-secondary)]">Hochladen...</span>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 mx-auto mb-2 text-[var(--text-tertiary)]" />
              <p className="text-[13px] text-[var(--text-secondary)] mb-1">{t('sales.assets.dropzone')}</p>
              <p className="text-[11px] text-[var(--text-tertiary)]">{t('sales.assets.allowedTypes')} · {t('sales.assets.maxSize')}</p>
              <label className="mt-3 inline-block px-4 py-2 rounded-[8px] text-[12px] font-medium text-[var(--primary)] border border-[var(--primary)] cursor-pointer hover:bg-primary-50">
                Datei auswählen
                <input type="file" className="hidden" accept=".pdf,.docx,.pptx,.png,.jpg,.jpeg"
                  onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0]); }} />
              </label>
            </>
          )}
        </div>
      )}

      {/* Assets Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border-table,#F3F4F6)]">
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">{t('sales.assets.name')}</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">{t('sales.assets.type')}</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">{t('sales.assets.size')}</th>
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase text-[var(--text-secondary)]">{t('sales.assets.uploadedAt')}</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {assets.map((a) => {
              const TypeIcon = TYPE_ICONS[a.typ] || FileText;
              return (
                <tr key={a.id} className="border-b border-[var(--border-table,#F3F4F6)] last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <TypeIcon className="w-4 h-4 text-[var(--text-tertiary)]" />
                      <span className="text-[13px] text-[var(--text-primary)]">{a.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase bg-[var(--bg-inset)] text-[var(--text-secondary)] border border-[var(--border-card)]">
                      {a.typ}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[12px] text-[var(--text-secondary)]">{formatSize(a.groesse)}</td>
                  <td className="px-4 py-2.5 text-[12px] text-[var(--text-tertiary)]">{formatRelative(a.created_at)}</td>
                  <td className="px-2 py-2.5">
                    <div className="flex items-center gap-1">
                      <button onClick={() => downloadAsset(a.id)} className="p-1.5 rounded text-[var(--text-tertiary)] hover:text-[var(--primary)] hover:bg-[var(--bg-inset)]" title={t('sales.assets.download')}>
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setDeleteConfirm(a.id)} className="p-1.5 rounded text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-50" title={t('sales.assets.delete')}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {assets.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[13px] text-[var(--text-tertiary)]">
                  {t('common.noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-[var(--bg-card)] rounded-2xl p-6 w-full max-w-sm shadow-xl border border-[var(--border-card)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">{t('common.confirmDelete') || 'Löschen bestätigen'}</h3>
                <p className="text-[13px] text-[var(--text-secondary)]">{t('common.deleteHint') || 'Diese Aktion kann nicht rückgängig gemacht werden.'}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-[var(--text-secondary)] border border-[var(--border-card)] hover:bg-[var(--bg-inset)]">
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button onClick={() => deleteAsset(deleteConfirm)} className="px-4 py-2 rounded-[8px] text-[13px] font-medium text-white bg-red-600 hover:bg-red-700">
                {t('common.delete') || 'Löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AgentTab() {
  const { t } = useLanguage();
  const [dateien, setDateien] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editFile, setEditFile] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const loadDateien = () => {
    api('/api/sales/agent/dateien')
      .then(setDateien)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDateien(); }, []);

  const openEdit = async (name) => {
    setErrorMsg('');
    try {
      const data = await api(`/api/sales/agent/dateien/${name}`);
      setEditFile(name);
      setEditContent(data.content);
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  const saveFile = async () => {
    setSaving(true);
    setErrorMsg('');
    try {
      await api(`/api/sales/agent/dateien/${editFile}`, {
        method: 'PUT',
        body: { content: editContent },
      });
      setEditFile(null);
      loadDateien();
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openTestChat = async () => {
    setErrorMsg('');
    try {
      const data = await api('/api/sales/agent/test');
      if (data.error) { setErrorMsg(data.error); return; }
      window.open(`/sales/chat/${data.token}`, '_blank');
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  if (loading) return <div className="text-sm text-[var(--text-secondary)]">Laden...</div>;

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {errorMsg && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-[8px] bg-red-50 border border-red-200 text-red-700 text-[13px]">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{errorMsg}</span>
          <button onClick={() => setErrorMsg('')} className="p-0.5 hover:bg-red-100 rounded">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-[var(--text-primary)] flex items-center gap-2">
            <Bot className="w-4 h-4 text-[var(--primary)]" />
            {t('sales.agent.knowledge') || 'Wissensbasis'}
          </h2>
          <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">
            {t('sales.agent.knowledgeDesc') || 'Dateien die der AI-Agent als Kontext nutzt'}
          </p>
        </div>
        <button
          onClick={openTestChat}
          className="flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12px] font-medium text-white"
          style={{ background: 'var(--primary)' }}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          {t('sales.agent.testAgent') || 'Agent testen'}
        </button>
      </div>

      {/* File List */}
      <div className="border border-[var(--border-card)] rounded-2xl overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] font-medium text-[var(--text-secondary)] uppercase border-b border-[var(--border-table,#F3F4F6)]">
              <th className="px-4 py-2.5">Datei</th>
              <th className="px-4 py-2.5">Größe</th>
              <th className="px-4 py-2.5">{t('sales.agent.lastUpdated') || 'Aktualisiert'}</th>
              <th className="px-4 py-2.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {dateien.map((d) => (
              <tr key={d.name} className="border-b border-[var(--border-table,#F3F4F6)] last:border-0 hover:bg-[var(--bg-card-hover)]">
                <td className="px-4 py-2.5 font-medium text-[var(--text-primary)] flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[var(--text-tertiary)]" />
                  {d.name}
                </td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">
                  {(d.size / 1024).toFixed(1)} KB
                </td>
                <td className="px-4 py-2.5 text-[var(--text-secondary)]">
                  {new Date(d.updated_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button
                    onClick={() => openEdit(d.name)}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-[6px] text-[11px] font-medium border border-[var(--border-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] ml-auto"
                  >
                    <Edit3 className="w-3 h-3" />
                    {t('sales.agent.editFile') || 'Bearbeiten'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editFile && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEditFile(null)}>
          <div className="bg-[var(--bg-card)] rounded-[12px] border border-[var(--border-card)] w-full max-w-[700px] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-table,#F3F4F6)]">
              <h3 className="text-[14px] font-semibold text-[var(--text-primary)] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[var(--primary)]" />
                {editFile}
              </h3>
              <button onClick={() => setEditFile(null)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <textarea
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                className="w-full h-[400px] rounded-[8px] border border-[var(--border-card)] p-3 text-[13px] font-mono text-[var(--text-primary)] bg-[var(--bg-input)] focus:outline-none focus:border-primary-500 resize-none"
              />
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-[var(--border-table,#F3F4F6)]">
              <button
                onClick={() => setEditFile(null)}
                className="px-4 py-2 rounded-[8px] text-[12px] font-medium border border-[var(--border-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)]"
              >
                {t('common.cancel') || 'Abbrechen'}
              </button>
              <button
                onClick={saveFile}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12px] font-medium text-white disabled:opacity-50"
                style={{ background: 'var(--primary)' }}
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? '...' : (t('sales.agent.saveFile') || 'Speichern')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VorlagenTab() {
  const { t } = useLanguage();
  const [vorlagen, setVorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aktiveVorlage, setAktiveVorlage] = useState(null);
  const [editName, setEditName] = useState('');
  const [editBeschreibung, setEditBeschreibung] = useState('');
  const [editAufgaben, setEditAufgaben] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [abtUsers, setAbtUsers] = useState([]);
  const [abtZuweisung, setAbtZuweisung] = useState({});

  const loadVorlagen = async () => {
    try {
      const rows = await api('/api/onboarding/vorlagen');
      setVorlagen(rows);
      if (!aktiveVorlage && rows.length) selectVorlage(rows[0]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadAbtZuweisung = async () => {
    try {
      const [zuw, users] = await Promise.all([
        api('/api/onboarding/abteilung-zuweisung'),
        api('/api/onboarding/users'),
      ]);
      setAbtZuweisung(zuw);
      setAbtUsers(users);
    } catch { /* */ }
  };

  useEffect(() => { loadVorlagen(); loadAbtZuweisung(); }, []);

  const selectVorlage = (v) => {
    setAktiveVorlage(v);
    setEditName(v.name);
    setEditBeschreibung(v.beschreibung || '');
    setEditAufgaben(Array.isArray(v.aufgaben) ? v.aufgaben.map((a, i) => ({ ...a, _key: i })) : []);
    setErrorMsg('');
  };

  const handleSave = async () => {
    if (!aktiveVorlage || !editName.trim()) return;
    setSaving(true);
    setErrorMsg('');
    try {
      const aufgabenClean = editAufgaben.map(({ _key, ...rest }) => rest);
      const updated = await api(`/api/onboarding/vorlagen/${aktiveVorlage.id}`, {
        method: 'PUT',
        body: { name: editName.trim(), beschreibung: editBeschreibung.trim(), aufgaben: aufgabenClean },
      });
      setVorlagen(prev => prev.map(v => v.id === updated.id ? updated : v));
      setAktiveVorlage(updated);
    } catch (err) { setErrorMsg(err.message); }
    finally { setSaving(false); }
  };

  const handleNeueVorlage = async () => {
    setErrorMsg('');
    try {
      const v = await api('/api/onboarding/vorlagen', {
        method: 'POST',
        body: { name: 'Neue Vorlage', aufgaben: [] },
      });
      setVorlagen(prev => [...prev, v]);
      selectVorlage(v);
    } catch (err) { setErrorMsg(err.message); }
  };

  const handleDelete = async (id) => {
    setErrorMsg('');
    try {
      const res = await api(`/api/onboarding/vorlagen/${id}`, { method: 'DELETE' });
      if (res.error) { setErrorMsg(res.error); return; }
      setVorlagen(prev => prev.filter(v => v.id !== id));
      if (aktiveVorlage?.id === id) {
        const remaining = vorlagen.filter(v => v.id !== id);
        if (remaining.length) selectVorlage(remaining[0]);
        else setAktiveVorlage(null);
      }
    } catch (err) { setErrorMsg(err.message); }
  };

  const handleSetStandard = async (id) => {
    try {
      await api(`/api/onboarding/vorlagen/${id}/standard`, { method: 'PUT' });
      await loadVorlagen();
    } catch (err) { setErrorMsg(err.message); }
  };

  const addAufgabe = () => {
    setEditAufgaben(prev => [...prev, {
      _key: Date.now(),
      titel: '',
      beschreibung: '',
      abteilung: 'customer_success',
      faellig_tage: 7,
      reihenfolge: prev.length,
    }]);
  };

  const updateAufgabe = (idx, field, value) => {
    setEditAufgaben(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const removeAufgabe = (idx) => {
    setEditAufgaben(prev => prev.filter((_, i) => i !== idx));
  };

  const moveAufgabe = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= editAufgaben.length) return;
    setEditAufgaben(prev => {
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next.map((a, i) => ({ ...a, reihenfolge: i }));
    });
  };

  if (loading) return <div className="text-center py-12 text-[var(--text-tertiary)] text-[13px]">Loading...</div>;

  return (
    <div>
      {errorMsg && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-[13px] mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg('')} className="p-0.5 hover:bg-red-100 rounded ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      <div className="flex gap-6">
        {/* Left: Vorlagen-Liste */}
        <div className="w-64 flex-shrink-0 space-y-2">
          {vorlagen.map(v => (
            <div
              key={v.id}
              onClick={() => selectVorlage(v)}
              className={`p-3 rounded-xl border cursor-pointer transition-colors ${
                aktiveVorlage?.id === v.id
                  ? 'border-[#338edc] bg-[#EBF4FD]'
                  : 'border-[var(--border-card)] hover:bg-[var(--bg-card-hover)]'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[var(--text-primary)]">{v.name}</span>
                {v.ist_standard && (
                  <span className="text-[10px] bg-[#EBF4FD] text-[#338edc] px-2 py-0.5 rounded-full font-medium">
                    {t('sales.onboarding.standard')}
                  </span>
                )}
              </div>
              <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                {(Array.isArray(v.aufgaben) ? v.aufgaben.length : 0)} {t('sales.onboarding.title') === 'Onboarding' ? 'Aufgaben' : 'Tasks'}
              </p>
            </div>
          ))}
          <button
            onClick={handleNeueVorlage}
            className="w-full p-3 rounded-xl border border-dashed border-[var(--border-card)] text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[#338edc] transition-colors flex items-center justify-center gap-1.5"
          >
            <Plus className="w-4 h-4" />
            {t('sales.onboarding.neueVorlage')}
          </button>
        </div>

        {/* Right: Detail */}
        {aktiveVorlage ? (
          <div className="flex-1 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-[12px] p-5">
            {/* Name + Beschreibung */}
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">
                  {t('sales.onboarding.vorlageName')}
                </label>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">
                  {t('sales.onboarding.beschreibung')}
                </label>
                <textarea
                  value={editBeschreibung}
                  onChange={(e) => setEditBeschreibung(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)] resize-none"
                />
              </div>
            </div>

            {/* Actions row */}
            <div className="flex items-center gap-2 mb-4">
              {!aktiveVorlage.ist_standard && (
                <button
                  onClick={() => handleSetStandard(aktiveVorlage.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium border border-[var(--border-input)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]"
                >
                  <Star className="w-3.5 h-3.5" />
                  {t('sales.onboarding.alsStandard')}
                </button>
              )}
              {!aktiveVorlage.ist_standard && (
                <button
                  onClick={() => handleDelete(aktiveVorlage.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium text-red-600 border border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Aufgaben */}
            <div className="mb-4">
              <h3 className="text-[12px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] mb-3">
                {t('sales.onboarding.title')} — {editAufgaben.length} {editAufgaben.length === 1 ? 'Aufgabe' : 'Aufgaben'}
              </h3>
              <div className="space-y-2">
                {editAufgaben.map((aufgabe, idx) => (
                  <div key={aufgabe._key} className="flex items-center gap-3 p-3 border border-[var(--border-card)] rounded-xl">
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <button onClick={() => moveAufgabe(idx, -1)} disabled={idx === 0} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-20">
                        <GripVertical size={14} />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <input
                        value={aufgabe.titel}
                        onChange={(e) => updateAufgabe(idx, 'titel', e.target.value)}
                        placeholder="Aufgabentitel..."
                        className="w-full text-sm font-medium bg-transparent border-none outline-none text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
                      />
                      <div className="flex gap-2 mt-1.5">
                        <select
                          value={aufgabe.abteilung || 'customer_success'}
                          onChange={(e) => updateAufgabe(idx, 'abteilung', e.target.value)}
                          className="text-xs border border-[var(--border-input)] rounded-lg px-2 py-1 bg-[var(--bg-card)] text-[var(--text-primary)]"
                        >
                          <option value="customer_success">{t('sales.onboarding.departments.customer_success')}</option>
                          <option value="it">{t('sales.onboarding.departments.it')}</option>
                          <option value="buchhaltung">{t('sales.onboarding.departments.buchhaltung')}</option>
                          <option value="sonstige">{t('sales.onboarding.departments.sonstige')}</option>
                        </select>
                        <div className="flex items-center gap-1 text-xs text-[var(--text-secondary)]">
                          <span>{t('sales.onboarding.faelligIn')}</span>
                          <input
                            type="number"
                            value={aufgabe.faellig_tage ?? 7}
                            onChange={(e) => updateAufgabe(idx, 'faellig_tage', parseInt(e.target.value) || 0)}
                            className="w-12 text-xs border border-[var(--border-input)] rounded-lg px-2 py-1 text-center bg-[var(--bg-card)] text-[var(--text-primary)]"
                          />
                          <span>{t('sales.onboarding.tagen')}</span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => removeAufgabe(idx)} className="flex-shrink-0">
                      <Trash2 size={14} className="text-[var(--text-tertiary)] hover:text-[#EF4444]" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={addAufgabe}
                className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-[var(--text-secondary)] hover:text-[#338edc]"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('sales.onboarding.aufgabeHinzufuegen')}
              </button>
            </div>

            {/* Save */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving || !editName.trim()}
                className="flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12px] font-medium text-white disabled:opacity-50"
                style={{ background: 'var(--primary)' }}
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? '...' : (t('common.save') || 'Speichern')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-[13px] text-[var(--text-tertiary)] py-12">
            {t('sales.onboarding.neueVorlage')}
          </div>
        )}
      </div>

      {/* Abteilungs-Standard-Zuweisung */}
      <div className="col-span-full mt-6 pt-6 border-t border-[var(--border-card)]">
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)] mb-1">
          {t('sales.onboarding.abteilungsZuweisung')}
        </h3>
        <p className="text-[11px] text-[var(--text-secondary)] mb-4">
          {t('sales.onboarding.abteilungsZuweisungDesc')}
        </p>
        {['customer_success', 'it', 'buchhaltung'].map(abt => (
          <div key={abt} className="flex items-center gap-3 mb-3">
            <span className="text-[12px] text-[var(--text-secondary)] w-36">
              {t(`sales.onboarding.departments.${abt}`)}
            </span>
            <select
              value={abtZuweisung[abt] || ''}
              onChange={async (e) => {
                const val = e.target.value ? parseInt(e.target.value) : '';
                setAbtZuweisung(prev => ({ ...prev, [abt]: val }));
                try {
                  await api('/api/onboarding/abteilung-zuweisung', {
                    method: 'PUT', body: { [abt]: val || '' },
                  });
                } catch { /* */ }
              }}
              className="text-[12px] border border-[var(--border-input)] rounded-lg px-3 py-1.5 bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
            >
              <option value="">{t('sales.onboarding.keinStandard')}</option>
              {abtUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

// #1533 — Per-User-M365-Connect (Schritt F / Weg B). Minimal-UI für den DEV-Pilot:
// Verbinden (Browser-Redirect zu Microsoft) / Status-Badge / Trennen. KEIN Send-Pfad.
function M365Tab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setStatus(await api('/api/sales/m365/status')); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Callback-Redirect-Hinweis (?m365=connected|error) auswerten
    const p = new URLSearchParams(window.location.search);
    const m = p.get('m365');
    if (m === 'connected') setMsg({ ok: true, text: `Verbunden${p.get('email') ? ' — ' + p.get('email') : ''}` });
    else if (m === 'error') setMsg({ ok: false, text: p.get('msg') || 'Verbindung fehlgeschlagen' });
  }, []);

  const connect = () => { window.location.href = `${API}/api/sales/m365/connect`; };

  const disconnect = async () => {
    setBusy(true);
    try { await api('/api/sales/m365/disconnect', { method: 'POST' }); setMsg({ ok: true, text: 'Getrennt' }); await load(); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  const connected = status?.connected;
  const reauth = status?.reauth_required || status?.status === 'reauth';

  return (
    <div className="max-w-[640px]">
      <div className="border border-[var(--border-card)] rounded-[12px] p-5 bg-[var(--bg-card)]">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">Microsoft 365 — persönliches Postfach</h3>
          {!loading && (
            <span className={`px-2.5 py-1 rounded-[8px] text-[12px] font-medium border ${
              connected ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : reauth ? 'bg-amber-50 border-amber-200 text-amber-700'
              : 'bg-[var(--bg-inset)] border-[var(--border-card)] text-[var(--text-tertiary)]'
            }`}>
              {connected ? 'Verbunden' : reauth ? 'Neu verbinden nötig' : 'Nicht verbunden'}
            </span>
          )}
        </div>
        <p className="text-[13px] text-[var(--text-secondary)] mb-4">
          Verbinde dein persönliches M365-Konto — es ist dann automatisch dein Owner-Konto:
          Workflow-Mails deiner Leads gehen über deinen eigenen Absender (Graph), Antworten
          in deinem Postfach werden automatisch dem Lead zugeordnet. Trennen gibt die
          manuelle SMTP/IMAP-Option im Tab „Owner-Konten" wieder frei.
        </p>

        {connected && status?.account_email && (
          <div className="text-[13px] text-[var(--text-secondary)] mb-4">
            Konto: <span className="text-[var(--text-primary)] font-medium">{status.account_email}</span>
          </div>
        )}

        <div className="flex items-center gap-3">
          {(!connected || reauth) && (
            <button
              onClick={connect}
              className="px-4 py-2 rounded-[8px] bg-[var(--primary,#338edc)] text-white text-[13px] font-medium hover:opacity-90 flex items-center gap-2"
            >
              <ExternalLink size={15} /> {reauth ? 'M365 neu verbinden' : 'M365 verbinden'}
            </button>
          )}
          {connected && (
            <button
              onClick={disconnect}
              disabled={busy}
              className="px-4 py-2 rounded-[8px] border border-red-200 bg-red-50 text-red-700 text-[13px] font-medium hover:bg-red-100 disabled:opacity-50"
            >
              {busy ? 'Trenne…' : 'Trennen'}
            </button>
          )}
        </div>

        {msg && (
          <div className={`mt-4 flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium ${
            msg.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {msg.ok ? <Check size={14} /> : <AlertCircle size={14} />} {msg.text}
          </div>
        )}
      </div>
    </div>
  );
}

// #1620 — Per-Owner-Sales-Mail-Konten (Admin-CRUD, Passwörter write-only)
const OWNER_EMPTY = {
  user_id: '', from_name: '', from_email: '',
  smtp_host: '', smtp_port: 587, smtp_user: '', smtp_pass: '',
  imap_host: '', imap_port: 993, imap_user: '', imap_pass: '',
  imap_tls: true, imap_mailbox: 'INBOX', imap_enabled: false, aktiv: true,
};

function OwnerAccountsTab() {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [m365Accounts, setM365Accounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | account.id
  const [form, setForm] = useState(OWNER_EMPTY);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [testing, setTesting] = useState(null); // { id, kind }
  const [testResults, setTestResults] = useState({}); // { [`${id}-smtp`]: {ok,msg} }

  const load = () => {
    api('/api/sales/owner-accounts')
      .then((d) => { setAccounts(d.accounts || []); setM365Accounts(d.m365Accounts || []); })
      .catch(() => { setAccounts([]); setM365Accounts([]); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api('/api/users').then((d) => setUsers((d.users || d || []).filter(u => u.aktiv !== false))).catch(() => setUsers([]));
  }, []);

  const flash = (ok, msg) => { setToast({ ok, msg }); setTimeout(() => setToast(null), 4000); };

  const startEdit = (acc) => {
    if (!acc) { setForm(OWNER_EMPTY); setEditing('new'); return; }
    setForm({
      ...OWNER_EMPTY, ...acc,
      smtp_pass: '', imap_pass: '', // write-only: leer = behalten
    });
    setEditing(acc.id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      delete body.id; delete body.user_name; delete body.user_email;
      delete body.has_smtp_pass; delete body.has_imap_pass;
      delete body.last_poll_at; delete body.test_status; delete body.test_message;
      delete body.created_at; delete body.updated_at;
      delete body.shadow_last_uid; delete body.shadow_uidvalidity;
      if (editing === 'new') await api('/api/sales/owner-accounts', { method: 'POST', body });
      else await api(`/api/sales/owner-accounts/${editing}`, { method: 'PUT', body });
      flash(true, t('sales.ownerAccounts.saved'));
      setEditing(null);
      load();
    } catch (err) {
      flash(false, err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleAktiv = async (acc) => {
    try {
      await api(`/api/sales/owner-accounts/${acc.id}`, { method: 'PUT', body: { aktiv: !acc.aktiv } });
      load();
    } catch (err) { flash(false, err.message); }
  };

  const runTest = async (acc, kind) => {
    setTesting({ id: acc.id, kind });
    try {
      const res = await api(`/api/sales/owner-accounts/${acc.id}/test-${kind}`, { method: 'POST' });
      setTestResults(prev => ({
        ...prev,
        [`${acc.id}-${kind}`]: res.success
          ? { ok: true, msg: kind === 'imap' ? `${t('sales.ownerAccounts.testOk')} (${res.messages ?? 0} Mails)` : t('sales.ownerAccounts.testOk') }
          : { ok: false, msg: res.error || 'Fehler' },
      }));
    } catch (err) {
      setTestResults(prev => ({ ...prev, [`${acc.id}-${kind}`]: { ok: false, msg: err.message } }));
    } finally {
      setTesting(null);
    }
  };

  if (loading) return <div className="text-[13px] text-[var(--text-tertiary)]">…</div>;

  return (
    <div className="max-w-4xl space-y-4">
      <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">{t('sales.ownerAccounts.title')}</h2>
          <button
            onClick={() => startEdit(null)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-white text-[12px] font-medium"
            style={{ background: 'var(--primary)' }}
          >
            <Plus className="w-3.5 h-3.5" /> {t('sales.ownerAccounts.add')}
          </button>
        </div>
        <p className="text-[12px] text-[var(--text-tertiary)] mb-4">{t('sales.ownerAccounts.hint')}</p>

        {accounts.length === 0 && m365Accounts.length === 0 && (
          <div className="text-[13px] text-[var(--text-tertiary)] py-4 text-center">{t('sales.ownerAccounts.empty')}</div>
        )}

        <div className="space-y-2">
          {/* #1621 — M365-verbundene User: automatisch Owner-Konto, read-only */}
          {m365Accounts.map((acc) => (
            <div key={`m365-${acc.user_id}`} className={`border border-[var(--border-card)] rounded-[10px] p-3 ${acc.aktiv ? '' : 'opacity-60'}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-[var(--text-primary)]">{acc.user_name || acc.user_email || `User #${acc.user_id}`}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">{t('sales.ownerAccounts.viaM365')}</span>
                    {acc.m365_status === 'reauth' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700">{t('sales.ownerAccounts.m365Reauth')}</span>}
                  </div>
                  <div className="text-[12px] text-[var(--text-tertiary)] truncate">
                    {acc.from_email} · {t('sales.ownerAccounts.m365RowHint')}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {accounts.map((acc) => {
            const smtpRes = testResults[`${acc.id}-smtp`];
            const imapRes = testResults[`${acc.id}-imap`];
            return (
              <div key={acc.id} className={`border border-[var(--border-card)] rounded-[10px] p-3 ${acc.aktiv ? '' : 'opacity-60'}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-[var(--text-primary)]">{acc.user_name || acc.user_email || `User #${acc.user_id}`}</span>
                      {!acc.aktiv && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset)] text-[var(--text-tertiary)]">{t('sales.ownerAccounts.inactive')}</span>}
                      {acc.imap_enabled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700">IMAP</span>}
                    </div>
                    <div className="text-[12px] text-[var(--text-tertiary)] truncate">
                      {acc.from_name ? `${acc.from_name} <${acc.from_email}>` : acc.from_email} · SMTP {acc.smtp_host || '—'}{acc.imap_enabled ? ` · IMAP ${acc.imap_host || '—'}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => runTest(acc, 'smtp')} disabled={!!testing}
                      className="px-2.5 py-1 rounded-[8px] border border-[var(--border-input)] text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50">
                      {testing?.id === acc.id && testing?.kind === 'smtp' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('sales.ownerAccounts.testSmtp')}
                    </button>
                    <button onClick={() => runTest(acc, 'imap')} disabled={!!testing}
                      className="px-2.5 py-1 rounded-[8px] border border-[var(--border-input)] text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50">
                      {testing?.id === acc.id && testing?.kind === 'imap' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('sales.ownerAccounts.testImap')}
                    </button>
                    <button onClick={() => startEdit(acc)}
                      className="px-2.5 py-1 rounded-[8px] border border-[var(--border-input)] text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <ToggleSwitch checked={!!acc.aktiv} onChange={() => toggleAktiv(acc)} />
                  </div>
                </div>
                {(smtpRes || imapRes) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {smtpRes && (
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${smtpRes.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                        {smtpRes.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} SMTP: {smtpRes.msg}
                      </span>
                    )}
                    {imapRes && (
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${imapRes.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                        {imapRes.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} IMAP: {imapRes.msg}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {editing !== null && (
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-4">
            {editing === 'new' ? t('sales.ownerAccounts.add') : t('sales.ownerAccounts.edit')}
          </h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[11px] font-medium text-[var(--text-secondary)] mb-1 uppercase">{t('sales.ownerAccounts.user')}</label>
              <select
                value={form.user_id || ''}
                onChange={(e) => setForm(prev => ({ ...prev, user_id: e.target.value }))}
                disabled={editing !== 'new'}
                className="w-full px-3 py-2 rounded-[8px] border border-[var(--border-input)] bg-[var(--bg-input)] text-[13px] focus:outline-none focus:border-[var(--primary)] disabled:opacity-60"
              >
                <option value="">—</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{[u.vorname, u.nachname].filter(Boolean).join(' ') || u.email}</option>
                ))}
              </select>
            </div>
            <SmtpField label={t('sales.ownerAccounts.fromEmail')} field="from_email" cfg={form} setCfg={setForm} />
            <SmtpField label={t('sales.ownerAccounts.fromName')} field="from_name" cfg={form} setCfg={setForm} />
          </div>

          <div className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase mb-2">SMTP</div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <SmtpField label="Host" field="smtp_host" cfg={form} setCfg={setForm} />
            <SmtpField label="Port" field="smtp_port" type="number" cfg={form} setCfg={setForm} />
            <SmtpField label="User" field="smtp_user" cfg={form} setCfg={setForm} />
            <PasswordField label={`${t('sales.ownerAccounts.password')}${editing !== 'new' ? ` (${t('sales.ownerAccounts.passwordKeep')})` : ''}`} field="smtp_pass" cfg={form} setCfg={setForm} />
          </div>

          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] font-semibold text-[var(--text-tertiary)] uppercase">IMAP</div>
            <label className="flex items-center gap-2 text-[12px] text-[var(--text-secondary)]">
              {t('sales.ownerAccounts.imapEnabled')}
              <ToggleSwitch checked={!!form.imap_enabled} onChange={(v) => setForm(prev => ({ ...prev, imap_enabled: typeof v === 'boolean' ? v : !prev.imap_enabled }))} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <SmtpField label="Host" field="imap_host" cfg={form} setCfg={setForm} />
            <SmtpField label="Port" field="imap_port" type="number" cfg={form} setCfg={setForm} />
            <SmtpField label="User" field="imap_user" cfg={form} setCfg={setForm} />
            <PasswordField label={`${t('sales.ownerAccounts.password')}${editing !== 'new' ? ` (${t('sales.ownerAccounts.passwordKeep')})` : ''}`} field="imap_pass" cfg={form} setCfg={setForm} />
            <SmtpField label="Mailbox" field="imap_mailbox" cfg={form} setCfg={setForm} />
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-[var(--border-card)]">
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-white text-[13px] font-medium disabled:opacity-50"
              style={{ background: 'var(--primary)' }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('common.save')}
            </button>
            <button
              onClick={() => setEditing(null)}
              className="px-4 py-2 rounded-[8px] border border-[var(--border-input)] text-[13px] text-[var(--text-secondary)]"
            >
              {t('common.cancel')}
            </button>
            {toast && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium ${toast.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
                {toast.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                <span>{toast.msg}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {editing === null && toast && (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-medium w-fit ${toast.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {toast.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
          <span>{toast.msg}</span>
        </div>
      )}
    </div>
  );
}

export default function SalesEinstellungen() {
  const { t } = useLanguage();
  const [tab, setTab] = useTabParam('smtp');

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-[var(--text-primary)]">{t('sales.settings.title')}</h1>
      </div>

      <div className="flex gap-1 bg-[var(--bg-inset)] rounded-2xl p-1 mb-6 w-fit">
        <TabButton active={tab === 'smtp'} label={t('sales.settings.smtp')} onClick={() => setTab('smtp')} />
        <TabButton active={tab === 'scoring'} label={t('sales.settings.scoring')} onClick={() => setTab('scoring')} />
        <TabButton active={tab === 'assets'} label={t('sales.assets.title')} onClick={() => setTab('assets')} />
        <TabButton active={tab === 'agent'} label={t('sales.agent.title') || 'AI-Agent'} onClick={() => setTab('agent')} />
        <TabButton active={tab === 'vorlagen'} label={t('sales.onboarding.vorlagen')} onClick={() => setTab('vorlagen')} />
        <TabButton active={tab === 'm365'} label="M365" onClick={() => setTab('m365')} />
        <TabButton active={tab === 'owner-accounts'} label={t('sales.ownerAccounts.tab')} onClick={() => setTab('owner-accounts')} />
      </div>

      {tab === 'smtp' && <SmtpImapTab />}
      {tab === 'scoring' && <ScoringTab />}
      {tab === 'assets' && <AssetsTab />}
      {tab === 'agent' && <AgentTab />}
      {tab === 'vorlagen' && <VorlagenTab />}
      {tab === 'm365' && <M365Tab />}
      {tab === 'owner-accounts' && <OwnerAccountsTab />}
    </div>
  );
}
